import React, { useRef, useCallback, useState } from 'react';
import { C } from './styles/theme';
import { resolveTeam } from './doublesHelper';
import { Ico, Icons } from './Icons';
import { formatDateIST } from '../../utils/dateUtils';
import Modal from './Modal';

const MATCH_H = 100;
const GAP = 40; // Increased base gap
const MIN_GAP = 100; // Minimum gap for small draws (increased from 80)
const CONN_W = 40;
const MATCH_W = 260;
const PLAYER_ROW_H = 38;
const DATE_TIME_H = 28;

export default function KnockoutBracket({ matches, players, seeds = [], onMatchClick, onPlayerSwap, onMatchSwap, editable = false, tournamentName = 'bracket', onRefresh, isDoubles = false }) {
  const seedMap = {};
  seeds.forEach(s => { seedMap[String(s.playerId)] = s.seedNumber; });
  const exportRef = useRef(null);
  const bracketRef = useRef(null);
  const [switchMode, setSwitchMode] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [matchSwapMode, setMatchSwapMode] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [zoom, setZoom] = useState(0.5);
  const [isPinching, setIsPinching] = useState(false);
  const lastPinchDistance = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef(null);

  const exportAsPng = useCallback(async () => {
    const bracket = bracketRef.current;
    if (!bracket) return;
    try {
      // Save current zoom and reset to 100% for export
      const currentZoom = zoom;
      setZoom(1);
      
      // Wait for zoom transition to complete
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const html2canvas = (await import('html2canvas')).default;
      const w = bracket.scrollWidth;
      const h = bracket.scrollHeight;
      const canvas = await html2canvas(bracket, { 
        scale: 3,
        width: w, 
        height: h, 
        windowWidth: w, 
        windowHeight: h, 
        backgroundColor: '#FFFFFF',
        scrollX: 0,
        scrollY: 0,
        useCORS: true,
        allowTaint: true,
        logging: false,
        imageTimeout: 0,
        removeContainer: true
      });
      const link = document.createElement('a');
      const sanitizedName = tournamentName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      link.download = `${sanitizedName}_draw.png`;
      link.href = canvas.toDataURL('image/png', 1.0);
      link.click();
      
      // Restore previous zoom
      setZoom(currentZoom);
    } catch { /* */ }
  }, [tournamentName, zoom]);

  // Touch gesture handlers for pinch-to-zoom
  const handleTouchStart = useCallback((e) => {
    if (e.touches.length === 2) {
      setIsPinching(true);
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(touch2.clientX - touch1.clientX, touch2.clientY - touch1.clientY);
      lastPinchDistance.current = distance;
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (e.touches.length === 2 && isPinching) {
      e.preventDefault();
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(touch2.clientX - touch1.clientX, touch2.clientY - touch1.clientY);
      
      if (lastPinchDistance.current) {
        const delta = distance - lastPinchDistance.current;
        const zoomDelta = delta * 0.005; // Sensitivity factor
        setZoom(prev => Math.max(0.5, Math.min(2, prev + zoomDelta)));
      }
      
      lastPinchDistance.current = distance;
    }
  }, [isPinching]);

  const handleTouchEnd = useCallback((e) => {
    if (e.touches.length < 2) {
      setIsPinching(false);
      lastPinchDistance.current = null;
    }
  }, []);

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fullscreen handlers
  const toggleFullscreen = useCallback(() => {
    if (!isFullscreen) {
      if (containerRef.current?.requestFullscreen) {
        containerRef.current.requestFullscreen();
      } else if (containerRef.current?.webkitRequestFullscreen) {
        containerRef.current.webkitRequestFullscreen();
      } else if (containerRef.current?.msRequestFullscreen) {
        containerRef.current.msRequestFullscreen();
      }
      setIsFullscreen(true);
    } else {
      // When exiting fullscreen, close any open modals first by triggering a custom event
      const closeModalsEvent = new CustomEvent('closeAllModals');
      document.dispatchEvent(closeModalsEvent);
      
      // Small delay to let modals close before exiting fullscreen
      setTimeout(() => {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen();
        }
        setIsFullscreen(false);
      }, 100);
    }
  }, [isFullscreen]);

  // Listen for fullscreen changes
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, []);

  if (!matches.length) return <p style={{ color: C.g400 }}>No bracket generated yet.</p>;

  // Filter matches based on search query
  const searchLower = searchQuery.toLowerCase();
  const matchesSearch = (m) => {
    if (!searchLower) return true;
    const t1 = resolveTeam(m.player1, players);
    const t2 = resolveTeam(m.player2, players);
    return t1.name.toLowerCase().includes(searchLower) || t2.name.toLowerCase().includes(searchLower);
  };

  const rounds = {};
  matches.forEach(m => { if (!rounds[m.roundIndex]) rounds[m.roundIndex] = []; rounds[m.roundIndex].push(m); });
  Object.values(rounds).forEach(r => r.sort((a, b) => a.matchIndex - b.matchIndex));
  const roundKeys = Object.keys(rounds).map(Number).sort((a, b) => a - b);
  const totalRounds = roundKeys.length;
  const firstRoundCount = rounds[roundKeys[0]]?.length || 0;
  
  // Use larger gap for small draws (4 players = 2 matches, needs more space)
  const effectiveGap = firstRoundCount <= 2 ? MIN_GAP : GAP;
  const totalHeight = firstRoundCount * MATCH_H + (firstRoundCount - 1) * effectiveGap;

  const PAD_L = 16;
  const LABEL_H = 30;

  const getMatchX = (col) => PAD_L + col * (MATCH_W + CONN_W);
  const getMatchY = (roundCol, matchIdx) => {
    const matchesInRound = Math.pow(2, totalRounds - 1 - roundCol);
    const blockH = totalHeight / matchesInRound;
    return LABEL_H + matchIdx * blockH + blockH / 2 - MATCH_H / 2;
  };
  const getMatchCenterY = (roundCol, matchIdx) => getMatchY(roundCol, matchIdx) + MATCH_H / 2;
  const getRowY = (roundCol, matchIdx, row) => {
    const matchTop = getMatchY(roundCol, matchIdx);
    return matchTop + (row === 0 ? 19 : 57);
  };

  // Find champion
  const finalMatch = matches.find(m => m.roundIndex === roundKeys[roundKeys.length - 1] && m.matchIndex === 0);
  const champTeam = finalMatch?.winner ? resolveTeam(finalMatch.winner, players) : null;
  const champ = finalMatch?.winner ? players.find(p => p.id === finalMatch.winner) : null;

  // Allow swap anytime in first round
  const canSwitch = editable && (onPlayerSwap || onMatchSwap);

  const handlePlayerClick = (e, matchId, playerId, slot, roundIndex, matchIndex) => {
    if (!canSwitch || !switchMode || !playerId || roundIndex !== 0) return;
    e.stopPropagation();

    if (!selectedPlayer) {
      setSelectedPlayer({ matchId, playerId, slot, roundIndex, matchIndex });
    } else {
      if (selectedPlayer.roundIndex === roundIndex && 
          !(selectedPlayer.matchId === matchId && selectedPlayer.slot === slot)) {
        if (onPlayerSwap) {
          onPlayerSwap(selectedPlayer.matchId, selectedPlayer.slot, matchId, slot);
        }
      }
      setSelectedPlayer(null);
    }
  };

  const handleMatchClick = (e, match) => {
    // Check if this is a bye match
    const isBye = match.status === 'completed' && (!match.player1 || !match.player2);
    
    // In match swap mode, allow clicking
    if (matchSwapMode && canSwitch && match.roundIndex === 0) {
      e.stopPropagation();
      if (!selectedMatch) {
        setSelectedMatch(match);
      } else {
        if (selectedMatch.roundIndex === match.roundIndex && selectedMatch.id !== match.id) {
          if (onMatchSwap) {
            onMatchSwap(selectedMatch.id, match.id);
          }
        }
        setSelectedMatch(null);
      }
      return;
    }
    
    // For normal click (not in swap mode), don't allow clicking on bye matches
    if (onMatchClick && !isBye) {
      onMatchClick(match);
    }
  };

  return (
    <div ref={containerRef} style={{ background: 'var(--card-bg)', borderRadius: 0, border: '1px solid var(--card-border)', overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,32,91,0.06)', ...(isFullscreen ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9997, borderRadius: 0, display: 'flex', flexDirection: 'column' } : {}) }}>
      <div style={{ borderBottom: '1px solid #E5E7EB' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px', gap: 10 }}>
          <span style={{ fontSize: 13, color: '#00205B', fontWeight: 700, letterSpacing: 0.5, fontFamily: "'Outfit', sans-serif", whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, flex: '0 1 auto' }}>TOURNAMENT BRACKET</span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ display: 'flex', border: '2px solid #00205B', borderRadius: 6, overflow: 'hidden' }}>
              <button onClick={() => setZoom(Math.max(0.5, zoom - 0.1))} disabled={zoom <= 0.5} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, padding: 0, background: '#00205B', color: '#fff', border: 'none', borderRight: '1px solid rgba(255,255,255,0.2)', cursor: zoom <= 0.5 ? 'not-allowed' : 'pointer', opacity: zoom <= 0.5 ? 0.5 : 1, transition: 'all 0.2s' }} title="Zoom Out">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
              </button>
              <button onClick={() => { setZoom(1); if (onRefresh) onRefresh(); }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, padding: 0, background: '#00205B', color: '#fff', border: 'none', borderRight: '1px solid rgba(255,255,255,0.2)', cursor: 'pointer', transition: 'all 0.2s' }} title="Reset Zoom & Refresh">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M3 21v-5h5"/></svg>
              </button>
              <button onClick={() => setZoom(Math.min(2, zoom + 0.1))} disabled={zoom >= 2} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, padding: 0, background: '#00205B', color: '#fff', border: 'none', cursor: zoom >= 2 ? 'not-allowed' : 'pointer', opacity: zoom >= 2 ? 0.5 : 1, transition: 'all 0.2s' }} title="Zoom In">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
              </button>
            </div>
            <button 
              onClick={toggleFullscreen}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, padding: 0, background: '#00205B', color: '#BDFF00', border: '2px solid #00205B', borderRadius: 6, cursor: 'pointer', transition: 'all 0.2s' }} 
              title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {isFullscreen ? (
                  <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"/>
                ) : (
                  <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
                )}
              </svg>
            </button>
            {
              <div style={{ position: 'relative' }} ref={menuRef}>
                <button
                  onClick={() => setMenuOpen(!menuOpen)}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    width: 26,
                    height: 26,
                    padding: 0,
                    background: '#00205B',
                    color: '#BDFF00',
                    border: '2px solid #00205B',
                    borderRadius: 6,
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  title="Draw Actions">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                    <circle cx="12" cy="12" r="2"/>
                    <circle cx="12" cy="5" r="2"/>
                    <circle cx="12" cy="19" r="2"/>
                  </svg>
                </button>
                {menuOpen && (
                  <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 8, background: '#FFFFFF', border: '2px solid #00205B', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,32,91,0.15)', minWidth: 180, zIndex: 1000 }}>
                    <button
                      onClick={() => {
                        exportAsPng();
                        setMenuOpen(false);
                      }}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '12px 16px', background: 'transparent', border: 'none', borderBottom: '1px solid #EEF1F6', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#00205B', textAlign: 'left', transition: 'background 0.2s' }}
                      onMouseEnter={(e) => e.target.style.background = '#F8F9FC'}
                      onMouseLeave={(e) => e.target.style.background = 'transparent'}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                      Export as Image
                    </button>
                    {canSwitch && (
                      <>
                        <button
                          onClick={() => {
                            setSwitchMode(!switchMode);
                            setSelectedPlayer(null);
                            setMatchSwapMode(false);
                            setSelectedMatch(null);
                            setMenuOpen(false);
                          }}
                          style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '12px 16px', background: 'transparent', border: 'none', borderBottom: '1px solid #EEF1F6', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#00205B', textAlign: 'left', transition: 'background 0.2s' }}
                          onMouseEnter={(e) => e.target.style.background = '#F8F9FC'}
                          onMouseLeave={(e) => e.target.style.background = 'transparent'}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="17 1 21 5 17 9"></polyline>
                            <path d="M3 11V9a4 4 0 0 1 4-4h14"></path>
                            <polyline points="7 23 3 19 7 15"></polyline>
                            <path d="M21 13v2a4 4 0 0 1-4 4H3"></path>
                          </svg>
                          Switch Players
                        </button>
                        <button
                          onClick={() => {
                            setMatchSwapMode(!matchSwapMode);
                            setSelectedMatch(null);
                            setSwitchMode(false);
                            setSelectedPlayer(null);
                            setMenuOpen(false);
                          }}
                          style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '12px 16px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#00205B', textAlign: 'left', transition: 'background 0.2s' }}
                          onMouseEnter={(e) => e.target.style.background = '#F8F9FC'}
                          onMouseLeave={(e) => e.target.style.background = 'transparent'}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="3" width="7" height="7"></rect>
                            <rect x="14" y="3" width="7" height="7"></rect>
                            <rect x="14" y="14" width="7" height="7"></rect>
                            <rect x="3" y="14" width="7" height="7"></rect>
                            <polyline points="14 8 10 8 10 4"></polyline>
                            <polyline points="10 16 14 16 14 20"></polyline>
                          </svg>
                          Switch Matches
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            }
          </div>
        </div>
      </div>
      {/* Search Bar */}
      <div style={{ padding: '12px 20px', borderBottom: '1px solid #E5E7EB', position: 'relative' }}>
        <svg style={{ position: 'absolute', left: 20, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8892A6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8"/>
          <path d="m21 21-4.35-4.35"/>
        </svg>
        <input
          placeholder="Search players in bracket..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          style={{ width: '100%', padding: '12px 0 12px 32px', fontSize: 14, border: 0, borderBottom: '1px solid #E5E8EF', borderRadius: 0, background: 'transparent', transition: 'border-color 0.2s', outline: 0, boxShadow: 'none' }}
          onFocus={e => { e.target.style.borderBottom = '2px solid #00205B'; e.target.style.outline = 'none'; e.target.style.boxShadow = 'none'; }}
          onBlur={e => { e.target.style.borderBottom = '1px solid #E5E8EF'; e.target.style.boxShadow = 'none'; }}
        />
      </div>
      {switchMode && (
        <div style={{ padding: '10px 20px', background: 'linear-gradient(90deg, rgba(67,160,71,0.1) 0%, rgba(67,160,71,0.05) 100%)', borderTop: '2px solid rgba(67,160,71,0.3)', borderBottom: '1px solid rgba(67,160,71,0.2)', fontSize: 11, color: '#43A047', fontWeight: 600, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, position: 'relative' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#43A047" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/>
          </svg>
          {selectedPlayer ? `✓ Player selected from Match ${selectedPlayer.matchIndex + 1}. Click another player in the first round to swap.` : 'Click on a player in the first round to select, then click another player to swap positions.'}
          <button onClick={() => { setSwitchMode(false); setSelectedPlayer(null); }} style={{ position: 'absolute', right: 20, background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', color: '#43A047' }} title="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      )}
      {matchSwapMode && (
        <div style={{ padding: '10px 20px', background: 'linear-gradient(90deg, rgba(30,136,229,0.1) 0%, rgba(30,136,229,0.05) 100%)', borderTop: '2px solid rgba(30,136,229,0.3)', borderBottom: '1px solid rgba(30,136,229,0.2)', fontSize: 11, color: '#1E88E5', fontWeight: 600, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, position: 'relative' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1E88E5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/>
          </svg>
          {selectedMatch ? '✓ Match selected. Click another match in the first round to swap.' : 'Click on a match in the first round to select, then click another match to swap entire matches.'}
          <button onClick={() => { setMatchSwapMode(false); setSelectedMatch(null); }} style={{ position: 'absolute', right: 20, background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', color: '#1E88E5' }} title="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      )}
      <div ref={exportRef} style={{ overflowX: 'auto', overflowY: isFullscreen ? 'auto' : 'visible', padding: '24px 28px 32px', WebkitOverflowScrolling: 'touch', background: '#FFFFFF', touchAction: isPinching ? 'none' : 'auto', ...(isFullscreen ? { flex: 1, minHeight: 0 } : {}) }} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
        <div ref={bracketRef} style={{ position: 'relative', width: PAD_L + totalRounds * (MATCH_W + CONN_W) + (champ ? 200 : 0) + PAD_L, height: totalHeight + LABEL_H + 20, background: '#FFFFFF', transform: `scale(${zoom})`, transformOrigin: 'top left', transition: 'transform 0.2s ease' }}>
          {/* Round labels */}
          {roundKeys.map((rIdx, col) => {
            const rm = rounds[rIdx];
            const label = rm[0]?.round || `Round ${rIdx + 1}`;
            return (
              <div key={`label-${rIdx}`} style={{ position: 'absolute', left: getMatchX(col), top: 0, width: MATCH_W, textAlign: 'center', fontWeight: 800, fontSize: 11, color: '#00205B', letterSpacing: 2, fontFamily: "'Outfit', sans-serif", padding: '5px 0', borderBottom: '2px solid #6B7280' }}>
                {label.toUpperCase()}
              </div>
            );
          })}
          {champ && (
            <div style={{ position: 'absolute', left: getMatchX(totalRounds), top: 0, width: 180, textAlign: 'center', fontWeight: 800, fontSize: 11, color: '#00205B', letterSpacing: 2, fontFamily: "'Outfit', sans-serif", padding: '10px 0', borderBottom: '2px solid #6B7280' }}>
              CHAMPION
            </div>
          )}

          {/* Connector lines (SVG) — spans full container so coords match card positions */}
          <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', shapeRendering: 'crispEdges' }}>
            {roundKeys.map((rIdx, col) => {
              if (col >= totalRounds - 1) return null;
              const rm = rounds[rIdx];
              return rm.map((m, mi) => {
                const hasWinner = m.status === 'completed' && m.winner;
                // Which row did the winner come from? 0 = p1 (top), 1 = p2 (bottom)
                const winnerRow = hasWinner ? (m.winner === m.player1 ? 0 : 1) : null;

                const x1 = getMatchX(col) + MATCH_W;
                const exitY = hasWinner ? getRowY(col, mi, winnerRow) : getMatchCenterY(col, mi);

                const nextMi = Math.floor(mi / 2);
                const nextCol = col + 1;
                const entryRow = mi % 2 === 0 ? 0 : 1;
                const entryY = getRowY(nextCol, nextMi, entryRow);

                const midX = x1 + CONN_W / 2;
                const x2 = getMatchX(nextCol);

                // Color: green if winner advanced, navy if live, grey if pending
                const lineColor = hasWinner ? '#43A047' : m.status === 'live' ? '#E53935' : '#D8DDE8';
                const sw = hasWinner ? '2' : m.status === 'live' ? '2.5' : '2';

                return (
                  <g key={`conn-${rIdx}-${mi}`}>
                    {/* Horizontal exit from match */}
                    <line x1={x1} y1={exitY} x2={midX} y2={exitY} stroke={lineColor} strokeWidth={sw} />
                    {/* Vertical join */}
                    <line x1={midX} y1={exitY} x2={midX} y2={entryY} stroke={lineColor} strokeWidth={sw} />
                    {/* Horizontal entry into next match */}
                    <line x1={midX} y1={entryY} x2={x2} y2={entryY} stroke={lineColor} strokeWidth={sw} />
                  </g>
                );
              });
            })}
            {/* Line to champion */}
            {champTeam && (() => {
              const lastCol = totalRounds - 1;
              const fm = rounds[roundKeys[roundKeys.length - 1]]?.[0];
              const winnerRow = fm?.winner === fm?.player1 ? 0 : 1;
              const x1 = getMatchX(lastCol) + MATCH_W;
              const y1 = getRowY(lastCol, 0, winnerRow);
              const x2 = getMatchX(totalRounds);
              return <line x1={x1} y1={y1} x2={x2} y2={y1} stroke="#43A047" strokeWidth="2" />;
            })()}
          </svg>

          {/* Match cards */}
          {roundKeys.map((rIdx, col) => {
            const rm = rounds[rIdx];
            return rm.map((m, mi) => {
              const p1 = resolveTeam(m.player1, players);
              const p2 = resolveTeam(m.player2, players);
              
              // Check if this is a bye match (completed with missing player)
              const mDone = m.status === 'completed';
              const isBye = mDone && (!m.player1 || !m.player2);
              
              // For bye matches, show "Bye" instead of "TBD"
              if (isBye) {
                if (!m.player1) { p1.name = 'Bye'; p1.isBye = true; }
                if (!m.player2) { p2.name = 'Bye'; p2.isBye = true; }
              }
              
              // Generate match label based on round
              const roundLabel = m.round || '';
              let matchLabel = '';
              if (roundLabel.includes('Semi')) {
                matchLabel = `SF${mi + 1}`;
              } else if (roundLabel.includes('Quarter')) {
                matchLabel = `QF${mi + 1}`;
              } else if (roundLabel.includes('Final')) {
                matchLabel = 'Final';
              } else {
                matchLabel = `M ${mi + 1}`;
              }
              
              // For doubles, show only first names
              const getDisplayName = (team) => {
                if (!isDoubles || !team.name) return team.name;
                // Check if it's a doubles team (contains ' / ')
                if (team.name.includes(' / ')) {
                  const players = team.name.split(' / ');
                  return players.map(p => p.split(' ')[0]).join(' / ');
                }
                return team.name;
              };
              
              const p1DisplayName = getDisplayName(p1);
              const p2DisplayName = getDisplayName(p2);
              const isLive = m.status === 'live';
              const isDone = mDone;
              const x = getMatchX(col);
              const y = getMatchY(col, mi);
              const isP1Selected = switchMode && selectedPlayer?.matchId === m.id && selectedPlayer?.slot === 'player1';
              const isP2Selected = switchMode && selectedPlayer?.matchId === m.id && selectedPlayer?.slot === 'player2';
              const isMatchSelected = matchSwapMode && selectedMatch?.id === m.id;
              const matchesSearchQuery = matchesSearch(m);
              const isHighlighted = searchQuery && matchesSearchQuery;
              return (
                <div key={m.id} style={{ position: 'absolute', left: x, top: y, width: MATCH_W, opacity: searchQuery && !matchesSearchQuery ? 0.3 : 1, transition: 'opacity 0.2s' }}>
                  <div onClick={(e) => handleMatchClick(e, m)} style={{ position: 'relative', background: '#FFFFFF', borderRadius: 10, overflow: 'hidden', border: `2px solid ${isMatchSelected ? '#1E88E5' : isLive ? '#E53935' : isDone ? '#43A047' : isBye ? '#FFA726' : '#E8EAED'}`, boxShadow: isMatchSelected ? '0 4px 12px rgba(30,136,229,0.4)' : isLive ? '0 2px 6px rgba(229,57,53,0.2)' : isDone ? '0 2px 6px rgba(67,160,71,0.2)' : '0 1px 4px rgba(0,0,0,0.08)', cursor: (matchSwapMode && canSwitch && m.roundIndex === 0) || (!isBye && onMatchClick) ? 'pointer' : 'default', transition: 'all 0.2s', transform: isMatchSelected ? 'scale(1.05)' : isLive ? 'scale(1.02)' : 'scale(1)', opacity: isBye && !matchSwapMode && !switchMode ? 0.7 : 1 }}>

                    <div 
                      onClick={(e) => handlePlayerClick(e, m.id, m.player1, 'player1', m.roundIndex, mi)}
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 6, 
                        padding: '10px 12px', 
                        fontSize: 13, 
                        height: PLAYER_ROW_H, 
                        cursor: switchMode && canSwitch && m.player1 && m.roundIndex === 0 ? 'pointer' : 'default',
                        border: isP1Selected ? '2px solid #43A047' : 'none',
                        margin: isP1Selected ? '-2px' : '0',
                        background: (mDone && m.winner === m.player1) ? 'linear-gradient(90deg, rgba(67,160,71,0.12) 0%, rgba(67,160,71,0.05) 100%)' : 'transparent',
                        borderLeft: (mDone && m.winner === m.player1) ? '3px solid #43A047' : '3px solid transparent',
                        ...((mDone && m.winner === m.player1) ? { fontWeight: 700, color: '#43A047' } : (mDone && m.winner && m.winner !== m.player1) ? { color: C.g300 } : p1.isBye ? { color: '#FF9800', fontWeight: 600 } : { color: C.g600 }) 
                      }}>
                      {seedMap[m.player1]
                        ? <span style={{ fontSize: 9, fontWeight: 800, color: 'var(--lime)', background: 'var(--navy)', padding: '2px 6px', borderRadius: 4, minWidth: 20, textAlign: 'center', flexShrink: 0, boxShadow: '0 2px 4px rgba(0,32,91,0.2)' }}>S{seedMap[m.player1]}</span>
                        : <span style={{ minWidth: 20, flexShrink: 0 }} />}
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{p1DisplayName}</span>
                      <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                        {m.score.sets.map((s, i) => {
                          const won = s.p1 > s.p2;
                          const isCurrent = isLive && i === m.score.sets.length - 1;
                          return <span key={i} style={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: won ? 800 : 500, fontFamily: "'Outfit', sans-serif", color: won ? 'var(--navy)' : C.g300, background: isCurrent ? 'rgba(229,57,53,0.12)' : won ? 'rgba(0,32,91,0.08)' : 'transparent', borderRadius: 6, border: isCurrent ? '2px solid #E53935' : won ? '1px solid rgba(0,32,91,0.15)' : 'none', boxShadow: won ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}>{s.p1}</span>;
                        })}
                      </div>
                    </div>
                    <div style={{ height: 1, background: '#D1D5DB' }} />
                    <div 
                      onClick={(e) => handlePlayerClick(e, m.id, m.player2, 'player2', m.roundIndex, mi)}
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 6, 
                        padding: '10px 12px', 
                        fontSize: 13, 
                        height: PLAYER_ROW_H, 
                        cursor: switchMode && canSwitch && m.player2 && m.roundIndex === 0 ? 'pointer' : 'default',
                        border: isP2Selected ? '2px solid #43A047' : 'none',
                        margin: isP2Selected ? '-2px' : '0',
                        background: (mDone && m.winner === m.player2) ? 'linear-gradient(90deg, rgba(67,160,71,0.12) 0%, rgba(67,160,71,0.05) 100%)' : 'transparent',
                        borderLeft: (mDone && m.winner === m.player2) ? '3px solid #43A047' : '3px solid transparent',
                        ...((mDone && m.winner === m.player2) ? { fontWeight: 700, color: '#43A047' } : (mDone && m.winner && m.winner !== m.player2) ? { color: C.g300 } : p2.isBye ? { color: '#FF9800', fontWeight: 600 } : { color: C.g600 }) 
                      }}>
                      {seedMap[m.player2]
                        ? <span style={{ fontSize: 9, fontWeight: 800, color: 'var(--lime)', background: 'var(--navy)', padding: '2px 6px', borderRadius: 4, minWidth: 20, textAlign: 'center', flexShrink: 0, boxShadow: '0 2px 4px rgba(0,32,91,0.2)' }}>S{seedMap[m.player2]}</span>
                        : <span style={{ minWidth: 20, flexShrink: 0 }} />}
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{p2DisplayName}</span>
                      <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                        {m.score.sets.map((s, i) => {
                          const won = s.p2 > s.p1;
                          const isCurrent = isLive && i === m.score.sets.length - 1;
                          return <span key={i} style={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: won ? 800 : 500, fontFamily: "'Outfit', sans-serif", color: won ? 'var(--navy)' : C.g300, background: isCurrent ? 'rgba(229,57,53,0.12)' : won ? 'rgba(0,32,91,0.08)' : 'transparent', borderRadius: 6, border: isCurrent ? '2px solid #E53935' : won ? '1px solid rgba(0,32,91,0.15)' : 'none', boxShadow: won ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}>{s.p2}</span>;
                        })}
                      </div>
                    </div>
                    {/* Date & Time Footer */}
                    <div style={{ padding: '6px 12px', fontSize: 10, color: C.g400, background: 'linear-gradient(180deg, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0.04) 100%)', borderTop: `1px solid #E8EAED`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ fontSize: 9, fontWeight: 700, color: '#5F6B80', background: 'rgba(0,32,91,0.05)', padding: '3px 8px', borderRadius: 6, letterSpacing: 0.5, border: '1px solid rgba(0,32,91,0.1)' }}>{matchLabel}</div>
                        {isLive && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 9, fontWeight: 800, color: '#E53935', background: 'rgba(229,57,53,0.1)', padding: '3px 8px', borderRadius: 6, letterSpacing: 0.8, border: '1px solid rgba(229,57,53,0.3)' }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#E53935', animation: 'pulse 1.5s infinite' }} />
                            LIVE
                          </div>
                        )}
                      </div>
                      {(m.date || m.time) && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {m.date && <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600 }}><Ico d={Icons.calendar} size={11} color={C.g400} /> {formatDateIST(m.date, { weekday: 'short', month: 'short', day: 'numeric' })}</span>}
                          {m.time && <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontWeight: 700, color: 'var(--navy)' }}><Ico d={Icons.clock} size={11} color='var(--navy)' /> {m.time}</span>}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            });
          })}

          {/* Champion card */}
          {champTeam && (() => {
            const isDoublesChamp = champTeam.name.includes(' / ');
            const champNames = isDoublesChamp ? champTeam.name.split(' / ') : [champTeam.name];
            
            return (
              <div style={{ position: 'absolute', left: getMatchX(totalRounds), top: getMatchY(totalRounds - 1, 0) - 15, width: 180 }}>
                <div style={{ textAlign: 'center', background: `linear-gradient(135deg, #FFD700 0%, #FFA500 100%)`, borderRadius: 16, padding: '24px 20px', color: '#000', boxShadow: '0 8px 24px rgba(255,215,0,0.4)', border: '3px solid #FFD700', position: 'relative', overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, background: 'rgba(255,255,255,0.2)', borderRadius: '50%' }} />
                  <div style={{ position: 'absolute', bottom: -30, left: -30, width: 100, height: 100, background: 'rgba(255,255,255,0.15)', borderRadius: '50%' }} />
                  <div style={{ position: 'relative', zIndex: 1 }}>
                    <div style={{ fontSize: 40, marginBottom: 8, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }}>🏆</div>
                    {isDoublesChamp ? (
                      <div>
                        <div style={{ fontWeight: 800, fontSize: 14, fontFamily: "'Outfit', sans-serif", marginBottom: 2, textShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>{champNames[0]}</div>
                        <div style={{ fontWeight: 800, fontSize: 14, fontFamily: "'Outfit', sans-serif", marginBottom: 4, textShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>{champNames[1]}</div>
                      </div>
                    ) : (
                      <div style={{ fontWeight: 800, fontSize: 15, fontFamily: "'Outfit', sans-serif", marginBottom: 4, textShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>{champTeam.name}</div>
                    )}
                    <div style={{ marginTop: 8, padding: '4px 12px', background: 'rgba(0,0,0,0.1)', borderRadius: 20, fontSize: 9, fontWeight: 800, letterSpacing: 1, display: 'inline-block' }}>CHAMPION</div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
